// Line-level diff, dependency-free.
//
// Written for the campaign behavior block (docs/pitch-authority-architecture-2026-07-28.md):
// ~300 lines of sales behavior that used to live in Python and now lives in the
// campaign row. The whole reason that move is safe is that a human can SEE what
// changed before it reaches a live call — a 25,000-character textarea with no diff
// is how you get an "aggressive, off-pitch" call that nothing but an ear catches.
//
// Classic LCS. At ~300×300 lines that's ~90k cells — trivial, and it keeps the
// bundle free of a diff dependency.

// LCS is O(n·m) in TIME and MEMORY: the DP table is (n+1)×(m+1) Uint32s, so 4000
// lines would allocate ~64 MB and a RangeError would throw mid-render. 1500 lines
// is ~9 MB and still far past any real behavior block (40,000 chars at a typical
// 60+ chars/line is well under 700 lines).
const MAX_LINES = 1500;

// Textareas hand back LF, but a stored value can arrive CRLF (a seed run on a
// CRLF checkout, an API write). Without this, one stray \r makes EVERY line read
// as changed — a diff that cries wolf gets ignored, which is the one failure this
// screen cannot afford.
function toLines(value) {
  return String(value ?? '').replace(/\r\n?/g, '\n').split('\n');
}

export function diffLines(before, after) {
  const a = toLines(before);
  const b = toLines(after);

  if (a.length > MAX_LINES || b.length > MAX_LINES) {
    return { tooLarge: true, rows: [], added: 0, removed: 0 };
  }

  // lcs[i][j] = length of the longest common subsequence of a[i…] and b[j…]
  const lcs = Array.from({ length: a.length + 1 }, () => new Uint32Array(b.length + 1));
  for (let i = a.length - 1; i >= 0; i--) {
    for (let j = b.length - 1; j >= 0; j--) {
      lcs[i][j] = a[i] === b[j] ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    }
  }

  const rows = [];
  let added = 0;
  let removed = 0;
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      rows.push({ type: 'same', text: a[i], beforeLine: i + 1, afterLine: j + 1 });
      i++;
      j++;
    } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
      rows.push({ type: 'removed', text: a[i], beforeLine: i + 1, afterLine: null });
      removed++;
      i++;
    } else {
      rows.push({ type: 'added', text: b[j], beforeLine: null, afterLine: j + 1 });
      added++;
      j++;
    }
  }
  while (i < a.length) {
    rows.push({ type: 'removed', text: a[i], beforeLine: i + 1, afterLine: null });
    removed++;
    i++;
  }
  while (j < b.length) {
    rows.push({ type: 'added', text: b[j], beforeLine: null, afterLine: j + 1 });
    added++;
    j++;
  }

  return { tooLarge: false, rows, added, removed };
}

// Collapse long runs of unchanged lines to `context` lines either side, the way a
// unified diff does. On a 300-line block a two-word change is otherwise invisible.
export function collapseUnchanged(rows, context = 3) {
  const keep = new Array(rows.length).fill(false);
  rows.forEach((r, idx) => {
    if (r.type === 'same') return;
    for (let k = Math.max(0, idx - context); k <= Math.min(rows.length - 1, idx + context); k++) {
      keep[k] = true;
    }
  });

  const out = [];
  let skipped = 0;
  rows.forEach((r, idx) => {
    if (keep[idx]) {
      if (skipped > 0) {
        out.push({ type: 'gap', count: skipped });
        skipped = 0;
      }
      out.push(r);
    } else {
      skipped++;
    }
  });
  if (skipped > 0) out.push({ type: 'gap', count: skipped });
  return out;
}
